import { Injectable } from "@angular/core";
import { AstroUtilsService } from "@core/services/astro-utils/astro-utils.service";
import { MeasurementData } from "@shared/components/misc/measuring-tool/measuring-tool.component";

/**
 * Represents a label's bounding box for collision detection
 */
interface LabelBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Priority level (lower number = higher priority, less likely to be moved) */
  priority: number;
  /** Type of label for special handling in collision resolution */
  type: "point" | "dimension";
}

/**
 * Service for measurement utility functions related to astronomical measurements
 * Provides functionality for label positioning, measurement encoding, and distance calculations
 */
@Injectable({
  providedIn: "root"
})
export class MeasurementUtilsService {
  // Label size constants
  private readonly POINT_RADIUS = 6;
  private readonly BASE_LABEL_DISTANCE = 24;
  private readonly HORIZONTAL_THRESHOLD = Math.PI / 8;
  private readonly VERTICAL_THRESHOLD = Math.PI / 12;
  private readonly HORIZONTAL_OFFSET = 15;
  private readonly COORD_LABEL_WIDTH = 150;
  private readonly COORD_LABEL_HEIGHT = 20;
  private readonly DIM_LABEL_WIDTH = 60;
  private readonly DIM_LABEL_HEIGHT = 20;
  private readonly LABEL_SPACING = 5;

  constructor(private astroUtilsService: AstroUtilsService) {}

  /**
   * Calculate display positions for coordinate labels with collision detection
   * Places labels for measurement start and end points with intelligent positioning
   * and collision avoidance for both dimension labels and between coordinate labels
   *
   * @param measurement - The measurement data object to update with label positions
   */
  updateCoordinateLabelPositions(measurement: MeasurementData): void {
    // Calculate base positions
    this._calculateInitialLabelPositions(measurement);

    // Add offsets for vertical lines
    this._adjustVerticalLineLabels(measurement);

    // Create label bounding boxes for collision detection
    const labels = this._createLabelBoundingBoxes(measurement);

    // Resolve collisions between coordinate and dimension labels
    this._resolveCoordinateAndDimensionLabelCollisions(labels, measurement);

    // Check if the two coordinate labels collide with each other
    this._resolveCoordinateLabelCollisions(labels, measurement);
  }

  /**
   * Encode measurements for URL sharing using a compact representation
   *
   * @param measurements - Array of measurement data objects to encode
   * @returns Base64 encoded string representation of the measurements
   */
  encodeMeasurementsForUrl(measurements: MeasurementData[]): string {
    // Create a minimal representation of each measurement
    const minimalMeasurements = measurements.map(m => ({
      // Required for positioning
      sX: Math.round(m.startX),
      sY: Math.round(m.startY),
      eX: Math.round(m.endX),
      eY: Math.round(m.endY),

      // Optional celestial coordinates (if available)
      sRa: m.startRa !== null ? Number(m.startRa.toFixed(6)) : null,
      sDec: m.startDec !== null ? Number(m.startDec.toFixed(6)) : null,
      eRa: m.endRa !== null ? Number(m.endRa.toFixed(6)) : null,
      eDec: m.endDec !== null ? Number(m.endDec.toFixed(6)) : null,

      // Display options
      sC: m.showCircle || false,
      sR: m.showRectangle || false,

      // Optional metadata (only if present)
      n: m.name || undefined,
      no: m.notes || undefined
    }));

    // JSON stringify and compress using base64 encoding
    return btoa(JSON.stringify(minimalMeasurements));
  }

  /**
   * Calculate initial label positions based on measurement geometry
   */
  private _calculateInitialLabelPositions(measurement: MeasurementData): void {
    const angle = Math.atan2(measurement.endY - measurement.startY, measurement.endX - measurement.startX);

    // Calculate offsets based on line orientation
    const { startDistance, endDistance } = this._calculateLabelDistances(angle);

    // Calculate initial positions
    measurement.startLabelX = measurement.startX - startDistance * Math.cos(angle);
    measurement.startLabelY = measurement.startY - startDistance * Math.sin(angle);

    measurement.endLabelX = measurement.endX + endDistance * Math.cos(angle);
    measurement.endLabelY = measurement.endY + endDistance * Math.sin(angle);
  }

  /**
   * Calculate appropriate label distances based on line angle
   */
  private _calculateLabelDistances(angle: number): { startDistance: number; endDistance: number } {
    const absAngle = Math.abs(angle);
    const horizontalness = Math.min(Math.abs(absAngle), Math.abs(absAngle - Math.PI));

    // Determine if line is nearly horizontal or vertical
    const isNearlyHorizontal = horizontalness < this.HORIZONTAL_THRESHOLD;

    // Calculate base distances
    const startLabelDistance = this.BASE_LABEL_DISTANCE;
    const endLabelDistance = this.BASE_LABEL_DISTANCE * 1.5;

    // Calculate the diagonal factor for non-horizontal/vertical lines
    const diagonalFactor = Math.abs(Math.sin(2 * angle)) / 1.0;

    // Calculate boosts for different orientations
    const startVerticalBoost = startLabelDistance * 0.8 * diagonalFactor;
    const endVerticalBoost = endLabelDistance * 0.8 * diagonalFactor;

    let startExtraDistance = 0;
    let endExtraDistance = 0;

    if (isNearlyHorizontal) {
      const horizontalFactor = 1 - horizontalness / this.HORIZONTAL_THRESHOLD;
      startExtraDistance = startLabelDistance * 3.5 * horizontalFactor + startVerticalBoost;
      endExtraDistance = endLabelDistance * 3.5 * horizontalFactor + endVerticalBoost;
    } else {
      startExtraDistance = startVerticalBoost;
      endExtraDistance = endVerticalBoost;
    }

    return {
      startDistance: startLabelDistance + this.POINT_RADIUS + startExtraDistance,
      endDistance: endLabelDistance + this.POINT_RADIUS + endExtraDistance
    };
  }

  /**
   * Add horizontal offset for labels on vertical lines
   */
  private _adjustVerticalLineLabels(measurement: MeasurementData): void {
    const angle = Math.atan2(measurement.endY - measurement.startY, measurement.endX - measurement.startX);

    const absAngle = Math.abs(angle);
    const isNearlyVertical = Math.abs(absAngle - Math.PI / 2) < this.VERTICAL_THRESHOLD;

    if (isNearlyVertical) {
      if (angle > 0) {
        // Line pointing downward
        measurement.startLabelX -= this.HORIZONTAL_OFFSET;
        measurement.endLabelX += this.HORIZONTAL_OFFSET;
      } else {
        // Line pointing upward
        measurement.startLabelX += this.HORIZONTAL_OFFSET;
        measurement.endLabelX -= this.HORIZONTAL_OFFSET;
      }
    }
  }

  /**
   * Create bounding boxes for all labels in the measurement
   */
  private _createLabelBoundingBoxes(measurement: MeasurementData): LabelBoundingBox[] {
    // Create bounding boxes for coordinate labels
    const labels: LabelBoundingBox[] = [
      // 1. Start point coordinates label
      this._createCoordinateLabelBox(measurement.startLabelX, measurement.startLabelY),

      // 2. End point coordinates label
      this._createCoordinateLabelBox(measurement.endLabelX, measurement.endLabelY)
    ];

    // Only add dimension labels if in rectangle mode
    if (measurement.showRectangle) {
      // Add dimension label bounding boxes
      this._addDimensionLabelBoxes(labels, measurement);
    }

    return labels;
  }

  /**
   * Create a coordinate label bounding box
   */
  private _createCoordinateLabelBox(x: number, y: number): LabelBoundingBox {
    return {
      x: x - this.COORD_LABEL_WIDTH / 2,
      y: y - this.COORD_LABEL_HEIGHT / 2,
      width: this.COORD_LABEL_WIDTH,
      height: this.COORD_LABEL_HEIGHT,
      priority: 2, // Medium priority
      type: "point"
    };
  }

  /**
   * Add dimension label bounding boxes to the labels array
   */
  private _addDimensionLabelBoxes(labels: LabelBoundingBox[], measurement: MeasurementData): void {
    // Calculate positions for dimension labels
    const minX = Math.min(measurement.startX, measurement.endX);
    const maxX = Math.max(measurement.startX, measurement.endX);
    const minY = Math.min(measurement.startY, measurement.endY);
    const maxY = Math.max(measurement.startY, measurement.endY);

    // Width dimension label (horizontal)
    const widthLabelX = minX + (maxX - minX) / 2 - this.DIM_LABEL_WIDTH / 2;
    const widthLabelY = maxY + 20;

    labels.push({
      x: widthLabelX,
      y: widthLabelY,
      width: this.DIM_LABEL_WIDTH,
      height: this.DIM_LABEL_HEIGHT,
      priority: 1, // Higher priority (less likely to move)
      type: "dimension"
    });

    // Height dimension label (vertical)
    const heightLabelX = maxX + 20;
    const heightLabelY = minY + (maxY - minY) / 2 - this.DIM_LABEL_HEIGHT / 2;

    labels.push({
      x: heightLabelX,
      y: heightLabelY,
      width: this.DIM_LABEL_WIDTH,
      height: this.DIM_LABEL_HEIGHT,
      priority: 1, // Higher priority
      type: "dimension"
    });
  }

  /**
   * Resolve collisions between coordinate labels and dimension labels
   */
  private _resolveCoordinateAndDimensionLabelCollisions(
    labels: LabelBoundingBox[],
    measurement: MeasurementData
  ): void {
    // We only need to check coordinate labels (0 and 1) against dimension labels (2+)
    for (let i = 0; i < 2; i++) {
      for (let j = 2; j < labels.length; j++) {
        if (this._doLabelsOverlap(labels[i], labels[j])) {
          const newPos = this._resolveCollision(labels[i], labels[j]);
          this._updateLabelPosition(i, labels, newPos, measurement);
        }
      }
    }
  }

  /**
   * Resolve collisions between the two coordinate labels themselves
   */
  private _resolveCoordinateLabelCollisions(labels: LabelBoundingBox[], measurement: MeasurementData): void {
    if (labels.length > 1 && this._doLabelsOverlap(labels[0], labels[1])) {
      const newPos = this._resolveCollision(labels[0], labels[1]);

      // We move the first label as it has the same priority as the second
      measurement.startLabelX = newPos.x + this.COORD_LABEL_WIDTH / 2;
      measurement.startLabelY = newPos.y + this.COORD_LABEL_HEIGHT / 2;

      // Update the bounding box for subsequent collision checks
      labels[0].x = newPos.x;
      labels[0].y = newPos.y;
    }
  }

  /**
   * Update label position in the measurement object based on index and new position
   */
  private _updateLabelPosition(
    index: number,
    labels: LabelBoundingBox[],
    newPos: { x: number; y: number },
    measurement: MeasurementData
  ): void {
    if (index === 0) {
      // Start label
      measurement.startLabelX = newPos.x + this.COORD_LABEL_WIDTH / 2;
      measurement.startLabelY = newPos.y + this.COORD_LABEL_HEIGHT / 2;
    } else {
      // End label
      measurement.endLabelX = newPos.x + this.COORD_LABEL_WIDTH / 2;
      measurement.endLabelY = newPos.y + this.COORD_LABEL_HEIGHT / 2;
    }

    // Update the label's position in our array for subsequent collision checks
    labels[index].x = newPos.x;
    labels[index].y = newPos.y;
  }

  /**
   * Check if two label bounding boxes overlap
   */
  private _doLabelsOverlap(label1: LabelBoundingBox, label2: LabelBoundingBox): boolean {
    return (
      label1.x < label2.x + label2.width &&
      label1.x + label1.width > label2.x &&
      label1.y < label2.y + label2.height &&
      label1.y + label1.height > label2.y
    );
  }

  /**
   * Resolve a collision between two labels by moving one of them
   * The label with higher priority number gets moved
   */
  private _resolveCollision(label1: LabelBoundingBox, label2: LabelBoundingBox): { x: number; y: number } {
    // Determine which label to move (higher priority number gets moved)
    const labelToMove = label1.priority > label2.priority ? label1 : label2;
    const fixedLabel = label1.priority > label2.priority ? label2 : label1;

    // Calculate overlap amounts
    const overlapX = Math.min(
      labelToMove.x + labelToMove.width - fixedLabel.x,
      fixedLabel.x + fixedLabel.width - labelToMove.x
    );

    const overlapY = Math.min(
      labelToMove.y + labelToMove.height - fixedLabel.y,
      fixedLabel.y + fixedLabel.height - labelToMove.y
    );

    // New position defaults to current position
    const newX = labelToMove.x;
    let newY = labelToMove.y;

    if (labelToMove.type === "point") {
      // For point labels, use position-aware collision resolution
      this._resolvePointLabelCollision(labelToMove, fixedLabel, overlapX, overlapY, newX, newY);
    } else if (labelToMove.type === "dimension") {
      // For dimension labels, mostly move vertically
      newY = this._calculateVerticalOffset(labelToMove, fixedLabel);
    }

    return { x: newX, y: newY };
  }

  /**
   * Resolve collision specifically for point-type labels
   */
  private _resolvePointLabelCollision(
    labelToMove: LabelBoundingBox,
    fixedLabel: LabelBoundingBox,
    overlapX: number,
    overlapY: number,
    newX: number,
    newY: number
  ): { x: number; y: number } {
    // Check if the point is to the left/right of what it's colliding with
    const isRightOfFixed = labelToMove.x > fixedLabel.x + fixedLabel.width / 2;
    const isBelowFixed = labelToMove.y > fixedLabel.y + fixedLabel.height / 2;

    // Move horizontally for wider collisions, vertically for taller ones
    if (overlapX < overlapY) {
      newX = isRightOfFixed
        ? fixedLabel.x + fixedLabel.width + this.LABEL_SPACING
        : fixedLabel.x - labelToMove.width - this.LABEL_SPACING;
    } else {
      newY = isBelowFixed
        ? fixedLabel.y + fixedLabel.height + this.LABEL_SPACING
        : fixedLabel.y - labelToMove.height - this.LABEL_SPACING;
    }

    return { x: newX, y: newY };
  }

  /**
   * Calculate vertical offset for dimension labels
   */
  private _calculateVerticalOffset(labelToMove: LabelBoundingBox, fixedLabel: LabelBoundingBox): number {
    return labelToMove.y > fixedLabel.y
      ? fixedLabel.y + fixedLabel.height + this.LABEL_SPACING
      : fixedLabel.y - labelToMove.height - this.LABEL_SPACING;
  }
}
